import { useTranslations } from "adapters/i18n";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import type { CategoryFormState } from "../category.types";
import { CategoryGender } from "../utils/category.utils";
import {
  CategoryNameError,
  validateCategoryAgeRange,
  validateCategoryGender,
  validateCategoryName,
} from "../utils/category.validators";
import { EnrichedAnimalCategory } from "shared/types";

type UseCategoryFormProps = {
  categories: EnrichedAnimalCategory[];
  onSubmit: (formState: CategoryFormState) => void;
  selectedCategory: EnrichedAnimalCategory | null;
};

export function useCategoryForm({
  categories,
  onSubmit,
  selectedCategory,
}: UseCategoryFormProps) {
  const { t } = useTranslations();

  const [formState, setFormState] = useState<CategoryFormState>({
    ageRangeId: { value: selectedCategory?.ageRangeId ?? null, error: null },
    headCount: { value: selectedCategory?.desiredNumber ?? null, error: null },
    gender: {
      value:
        (selectedCategory?.gender as CategoryGender | undefined) ??
        CategoryGender.Male,
      error: null,
    },
    initialAmountForConciliation: {
      value: selectedCategory?.initialNumberConference ?? null,
      error: null,
    },
    name: { value: selectedCategory?.name ?? "", error: null },
    weightGainCurveId: {
      value: selectedCategory?.weightGainCurveId ?? null,
      error: null,
    },
  });

  const nameErrorToMessage: Record<CategoryNameError, string> = {
    [CategoryNameError.Required]: t("common:validation.required-field"),
    [CategoryNameError.Duplicated]: t(
      "animalCategory:duplicate-category-name-message"
    ),
  };

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();
    const { error } = validateCategoryName(
      value,
      categories,
      selectedCategory?.id ?? null
    );
    const errorMessage = error != null ? nameErrorToMessage[error] : null;

    setFormState((prevState) => ({
      ...prevState,
      name: { value, error: errorMessage },
    }));
  };

  const handleChangeHeadCount = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);

    if (Number.isNaN(value) || !Number.isInteger(value)) {
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      headCount: { value, error: null },
    }));
  };

  const handleChangeInitialAmountForConciliation = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(event.target.value);

    if (Number.isNaN(value) || !Number.isInteger(value)) {
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      initialAmountForConciliation: { value, error: null },
    }));
  };

  const handleChangeGender = (value: CategoryGender) => {
    setFormState((prevState) => ({
      ...prevState,
      gender: { value, error: null },
      ageRangeId: { value: null, error: null },
    }));
  };

  const handleChangeAgeRange = (value: number) => {
    setFormState((prevState) => ({
      ...prevState,
      ageRangeId: { value, error: null },
    }));
  };

  const handleChangeWeightGainCurve = (value: number) => {
    setFormState((prevState) => ({
      ...prevState,
      weightGainCurveId: { value, error: null },
    }));
  };

  const handleResetForm = () => {
    setFormState({
      ageRangeId: { value: null, error: null },
      headCount: { value: null, error: null },
      gender: { value: CategoryGender.Male, error: null },
      initialAmountForConciliation: { value: null, error: null },
      name: { value: "", error: null },
      weightGainCurveId: { value: null, error: null },
    });
  };

  const canSubmitForm = useMemo(() => {
    const { isValid: isAgeRangeValid } = validateCategoryAgeRange(
      formState.ageRangeId.value
    );
    const { isValid: isGenderValid } = validateCategoryGender(
      formState.gender.value
    );
    const { isValid: isNameValid } = validateCategoryName(
      formState.name.value,
      categories,
      selectedCategory?.id ?? null
    );

    return isAgeRangeValid && isGenderValid && isNameValid;
  }, [
    categories,
    formState.ageRangeId.value,
    formState.gender.value,
    formState.name.value,
    selectedCategory?.id,
  ]);

  const handleSubmitForm = useCallback(async () => {
    if (!canSubmitForm) {
      return null;
    }

    onSubmit(formState);
  }, [canSubmitForm, formState, onSubmit]);

  return {
    canSubmitForm,
    formState,
    handleChangeAgeRange,
    handleChangeGender,
    handleChangeHeadCount,
    handleChangeInitialAmountForConciliation,
    handleChangeName,
    handleChangeWeightGainCurve,
    handleResetForm,
    handleSubmitForm,
  };
}
